import { create } from 'zustand';

/** Board and interface animations: follow the OS "reduce motion" setting, or force them on or off. */
export type MotionPref = 'system' | 'on' | 'off';

const KEY = 'vatman.motion';

function read(): MotionPref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'system' || v === 'on' || v === 'off') return v;
  } catch {
    // Storage may be unavailable (private mode); follow the system.
  }
  return 'system';
}

const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

function resolve(pref: MotionPref): boolean {
  if (pref === 'system') return !media?.matches;
  return pref === 'on';
}

function apply(enabled: boolean) {
  document.documentElement.dataset.motion = enabled ? 'on' : 'off';
}

interface MotionState {
  pref: MotionPref;
  /** Whether animations play right now. */
  enabled: boolean;
  setPref: (p: MotionPref) => void;
}

export const useMotion = create<MotionState>((set) => ({
  pref: read(),
  enabled: resolve(read()),
  setPref: (pref) => {
    try {
      localStorage.setItem(KEY, pref);
    } catch {
      // Ignore: the choice still applies for this session.
    }
    const enabled = resolve(pref);
    apply(enabled);
    set({ pref, enabled });
  },
}));

if (typeof document !== 'undefined') apply(useMotion.getState().enabled);

media?.addEventListener('change', () => {
  const { pref } = useMotion.getState();
  if (pref !== 'system') return;
  const enabled = resolve(pref);
  apply(enabled);
  useMotion.setState({ enabled });
});
